import React, { useState } from 'react';
import { uploadFile, compressImageForUpload } from '../firebase';
import { getSocket, isSocketConnected } from '../socket';
import { getDefaultAvatarWithInitial } from '../utils/defaultAvatar';
import './ProjectSettings.css';

function ProjectSettings({ project, user, friends, onClose }) {
  const [name, setName] = useState(project.name || '');
  const [description, setDescription] = useState(project.description || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [previewURL, setPreviewURL] = useState(project.photoURL || null);
  const [assetBoard, setAssetBoard] = useState(!!project.features?.assetBoard);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isOwner = project.ownerId === user.uid;

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be smaller than 5MB'); return; }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewURL(reader.result);
    reader.readAsDataURL(file);
    setError('');
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Project name is required'); return; }
    setLoading(true);
    setError('');
    try {
      let photoURL = project.photoURL;
      if (photoFile) {
        const compressed = await compressImageForUpload(photoFile, { maxDimension: 512, quality: 0.8 });
        const path = `project-files/${project.projectId}/cover_${Date.now()}.jpg`;
        photoURL = await uploadFile(compressed || photoFile, path);
      }

      const socket = getSocket();
      if (socket && isSocketConnected()) {
        socket.emit('project:update', {
          projectId: project.projectId,
          name: name.trim(),
          description: description.trim(),
          photoURL,
          features: { ...(project.features || {}), assetBoard }
        });
      }
      setSuccess('Project updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to update project');
    }
    setLoading(false);
  };

  const handleAddMember = (friendId) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:members:add', { projectId: project.projectId, userId: friendId });
    }
  };

  const handleRemoveMember = (memberId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:members:remove', { projectId: project.projectId, userId: memberId });
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this project and all its files? This cannot be undone.')) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:delete', { projectId: project.projectId });
      onClose();
    }
  };

  // Friends not yet in the project
  const invitableFriends = friends.filter(f => !project.members?.includes(f.userId));

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ps-header">
          <h3>Project Settings</h3>
          <button className="ps-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="ps-body">
          {/* Photo */}
          <div className="ps-photo-section">
            <div className="ps-photo-preview">
              {previewURL ? (
                <img src={previewURL} alt="Project" />
              ) : (
                <span className="material-symbols-outlined">folder_special</span>
              )}
            </div>
            <label className="ps-photo-btn">
              <span className="material-symbols-outlined">photo_camera</span>
              Change Photo
              <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
            </label>
          </div>

          {/* Name & Description */}
          <div className="ps-field">
            <label>Project Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name"
            />
          </div>
          <div className="ps-field">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </div>

          <div className="ps-field ps-toggle-field">
            <label htmlFor="ps-assetboard-toggle">
              Game-dev tabs
              <span className="ps-toggle-hint">Overview + Asset Board for tracking 3D model work</span>
            </label>
            <input
              id="ps-assetboard-toggle"
              type="checkbox"
              checked={assetBoard}
              onChange={(e) => setAssetBoard(e.target.checked)}
            />
          </div>

          {error && <div className="ps-error">{error}</div>}
          {success && <div className="ps-success">{success}</div>}

          <button className="ps-save-btn" onClick={handleSave} disabled={loading || !name.trim()}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>

          {/* Members */}
          <div className="ps-section">
            <h4>Members ({project.members?.length || 1})</h4>
            <div className="ps-member-list">
              {project.members?.map(memberId => {
                const friend = friends.find(f => f.userId === memberId);
                const memberName = memberId === user.uid ? 'You' : (friend?.username || 'Unknown');
                const isProjectOwner = memberId === project.ownerId;
                return (
                  <div key={memberId} className="ps-member-item">
                    <img
                      src={friend?.photoURL || getDefaultAvatarWithInitial(memberName)}
                      alt={memberName}
                      className="ps-member-avatar"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultAvatarWithInitial(memberName);
                      }}
                    />
                    <span className="ps-member-name">
                      {memberName}
                      {isProjectOwner && <span className="ps-owner-badge">Owner</span>}
                    </span>
                    {isOwner && !isProjectOwner && (
                      <button className="ps-member-remove" onClick={() => handleRemoveMember(memberId)}>
                        <span className="material-symbols-outlined">person_remove</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add Members */}
          {invitableFriends.length > 0 && (
            <div className="ps-section">
              <h4>Add Members</h4>
              <div className="ps-member-list">
                {invitableFriends.map(friend => (
                  <div key={friend.userId} className="ps-member-item ps-invitable">
                    <img
                      src={friend.photoURL || getDefaultAvatarWithInitial(friend.username)}
                      alt={friend.username}
                      className="ps-member-avatar"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getDefaultAvatarWithInitial(friend.username);
                      }}
                    />
                    <span className="ps-member-name">{friend.username}</span>
                    <button className="ps-member-add" onClick={() => handleAddMember(friend.userId)}>
                      <span className="material-symbols-outlined">person_add</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delete */}
          {isOwner && (
            <div className="ps-danger-zone">
              <button className="ps-delete-btn" onClick={handleDelete}>
                <span className="material-symbols-outlined">delete_forever</span>
                Delete Project
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectSettings;
